import apiService from 'src/shared/Services/apiService';
import type { ConsentArtefactRequest } from 'src/shared/Services/apiService/ConsentTypes';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  CONSENT OUTBOX
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  `consents/record` does not exist on the backend yet. Onboarding used to fail
 *  closed against it, which is the right posture for a live service but leaves
 *  the app unusable while the endpoint is being built.
 *
 *  Failing open on its own would be the wrong fix: the patient's choices would
 *  simply evaporate, which is the exact problem this whole workstream exists to
 *  solve. So instead of discarding the artefact, we QUEUE it and let the patient
 *  continue. The consent is still captured, still timestamped at the moment it
 *  was actually given (`clientRecordedAt` travels with the artefact), and is
 *  flushed to the server the moment the endpoint answers.
 *
 *  This is an outbox, not a substitute for the ledger:
 *
 *   • It lives in localStorage, so it is per-browser and a user who clears their
 *     data loses anything not yet flushed.
 *   • It is not tamper-evident. A queued artefact is weaker evidence than a
 *     server-stamped one.
 *
 *  Both are acceptable while the endpoint is unbuilt and NOT acceptable
 *  afterwards. Once `consents/record` is live, set
 *  `REACT_APP_CONSENT_STRICT=true` so a failed write blocks again and the
 *  outbox is reduced to what it should be — a retry buffer for transient
 *  network failures.
 */

const OUTBOX_KEY = 'symbian_consent_outbox';
/** Guard against unbounded growth if the endpoint stays down for a long time. */
const MAX_QUEUED = 50;

/**
 * When true, a failed write blocks the patient (the original fail-closed
 * behaviour). Turn this on once the backend endpoint is live in an environment.
 */
export const CONSENT_STRICT_MODE = process.env.REACT_APP_CONSENT_STRICT === 'true';

interface QueuedArtefact {
  /** Local-only id, so a flush can report what it sent. */
  queueId: string;
  artefact: ConsentArtefactRequest;
  queuedAt: string;
  attempts: number;
}

const read = (): QueuedArtefact[] => {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (items: QueuedArtefact[]): void => {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(items.slice(-MAX_QUEUED)));
  } catch {
    /* private mode or quota — nothing useful to do */
  }
};

const newId = (): string =>
  globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** Queue an artefact whose write failed. */
export const enqueueConsent = (artefact: ConsentArtefactRequest): void => {
  const items = read();
  items.push({
    queueId: newId(),
    artefact,
    queuedAt: new Date().toISOString(),
    attempts: 1,
  });
  write(items);
};

export const pendingConsentCount = (): number => read().length;

/**
 * Is there a queued onboarding artefact for this bundle version?
 *
 * Needed because the server is the normal source of truth for "has this patient
 * consented", and a queued artefact is invisible to it. Without this the
 * patient would finish onboarding, the artefact would sit in the outbox, the
 * next `GET /consents` would still say "never consented", and the stepper would
 * reopen — asking again for consent they just gave.
 */
export const hasQueuedConsentForBundle = (bundleVersion: string): boolean =>
  read().some(
    (q) =>
      q.artefact?.bundleVersion === bundleVersion &&
      q.artefact?.surface === 'onboarding',
  );

/**
 * Has a decision on this specific consent already been queued?
 *
 * Lets the just-in-time prompts (AI, recording) avoid re-asking something the
 * patient answered while the endpoint was unavailable.
 */
export const hasQueuedDecision = (consentId: string): boolean =>
  read().some((q) => q.artefact?.decisions?.some((d) => d.consentId === consentId));

/**
 * The queued answer for one consent, or null if none is queued.
 */
export const queuedDecisionValue = (consentId: string): boolean | null => {
  const items = read();
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const hit = items[i].artefact?.decisions?.find((d) => d.consentId === consentId);
    if (hit) return hit.granted;
  }
  return null;
};

/**
 * Try to send everything queued, oldest first.
 *
 * Stops at the first failure rather than hammering a dead endpoint, and
 * preserves order so the ledger receives decisions in the sequence the patient
 * actually made them — which matters when a later artefact supersedes an
 * earlier one.
 *
 * Returns how many were accepted.
 */
export const flushConsentOutbox = async (): Promise<number> => {
  const items = read();
  if (items.length === 0) return 0;

  let sent = 0;
  const remaining = [...items];

  while (remaining.length > 0) {
    const next = remaining[0];
    try {
      await apiService.recordConsent(next.artefact);
      remaining.shift();
      sent += 1;
    } catch (err: any) {
      const status = err?.response?.status;
      // A 4xx that is not auth-related means the server rejected this artefact
      // on its merits; retrying forever would block everything behind it.
      if (status && status >= 400 && status < 500 && status !== 401 && status !== 403) {
        remaining.shift();
        console.warn('[consent] Server rejected a queued artefact; dropping it.', status);
        continue;
      }
      next.attempts += 1;
      break; // Endpoint still unavailable — stop and try again later.
    }
  }

  write(remaining);
  return sent;
};
